import type { BillingMatrixCategory, BillingMatrixCell, BillingMatrixRow } from "../../../../../../shared/frontend/billing/billingPlanMatrixTypes";
import type { SharedBillingPlan } from "../../../../../../shared/frontend/billing/sharedBillingPageTypes";
import {
  M1MEMBER_QUANTITY_LIMITS,
  describePlanAiQuota,
  describePlanLocations,
  describePlanQuantityLimit,
} from "./m1memberPlanLimits";

type Translate = (key: string) => string;

const valuesByPlan = (plans: SharedBillingPlan[], cell: (plan: SharedBillingPlan) => BillingMatrixCell) =>
  Object.fromEntries(plans.map((plan) => [plan.id, cell(plan)]));

/**
 * Zeilen aus dem Katalog: Standorte aus Mindest-/Höchstanzahl, je gesetztem Mengenlimit eine Zeile.
 * Ein Limit, das kein Tarif setzt, bekommt keine Zeile — dort gibt es nichts zu vergleichen.
 */
function catalogLimitRows(plans: SharedBillingPlan[], tr: Translate): BillingMatrixRow[] {
  const rows: BillingMatrixRow[] = [
    {
      key: "location_count",
      label: tr("Locations"),
      description: tr("number of usable studio locations"),
      values: valuesByPlan(plans, (plan) => ({ status: "custom", ...describePlanLocations(plan, tr) })),
    },
  ];
  for (const limit of M1MEMBER_QUANTITY_LIMITS) {
    if (!plans.some((plan) => typeof plan.limits?.[limit.key] === "number")) continue;
    rows.push({
      key: limit.key,
      label: tr(limit.label),
      values: valuesByPlan(plans, (plan) => ({
        status: "custom",
        text: describePlanQuantityLimit(plan, limit.key, limit.upTo, tr) ?? tr("Unlimited"),
      })),
    });
  }
  return rows;
}

/**
 * Detaillierte Feature-Vergleichsmatrix für m1member-Tarife.
 * Kategorisiert in 5 Bereiche mit einheitlicher horizontaler Zeilenflucht.
 */
export function getM1MemberBillingMatrix(tr: Translate): (plans: SharedBillingPlan[]) => BillingMatrixCategory[] {
  return (plans) => [
    {
      key: "locations",
      title: tr("Locations & Branches"),
      rows: [
        ...catalogLimitRows(plans, tr),
        {
          key: "multi_studio",
          label: "zentrale Filialverwaltung & Multi-Studio",
          description: "mandantenübergreifende Daten & Auswertungen",
          values: {
            start: { status: "unavailable" },
            studio: { status: "included" },
            premium: { status: "included" },
          },
        },
      ],
    },
    {
      key: "members_finance",
      title: tr("Members, Finance & Access"),
      rows: [
        {
          key: "crm",
          label: "Mitglieder- & Interessentenverwaltung (CRM)",
          description: "Stammdaten, Akten, Verträge, Ruhezeiten",
          values: {
            start: { status: "included" },
            studio: { status: "included" },
            premium: { status: "included" },
          },
        },
        {
          key: "sepa",
          label: "Beitragskalender & SEPA-Lastschriften",
          description: "Forderungen, Bankeinzüge & Zahlläufe",
          values: {
            start: { status: "included" },
            studio: { status: "included" },
            premium: { status: "included" },
          },
        },
        {
          key: "checkin",
          label: "Check-in, Zutritt & Drehkreuz",
          description: "Live-Prüfung, RFID/QR, Türöffnungen",
          values: {
            start: { status: "included" },
            studio: { status: "included" },
            premium: { status: "included" },
          },
        },
        {
          key: "debt_collection",
          label: "Mahnwesen & Inkasso-Übergabe",
          description: "Mahnstufen, Gebühren & Inkasso-Export",
          values: {
            start: { status: "custom", text: "Basis", hint: "Zahlungserinnerung" },
            studio: { status: "included" },
            premium: { status: "included" },
          },
        },
      ],
    },
    {
      key: "operations_app",
      title: tr("Studio Operations & App"),
      rows: [
        {
          key: "member_app",
          label: "Mitglieder-App (iOS & Android)",
          description: "Check-in per QR, Self-Service, Vertragsdaten",
          values: {
            start: { status: "addon", text: "+ 29 € / Mo." },
            studio: { status: "included" },
            premium: { status: "included" },
          },
        },
        {
          key: "online_signup",
          label: "Online-Abschluss & Terminbuchung",
          description: "Website-Widgets für Neukunden-Abschluss & Online-Probetrainings",
          values: {
            start: { status: "addon", text: "+ 29 € / Mo." },
            studio: { status: "included" },
            premium: { status: "included" },
          },
        },
        {
          key: "pos",
          label: "Kasse & Thekenverkauf",
          description: "Artikelverwaltung, Snacks & Drinks, Bar-/Kartenzahlung & Verzehrkonto",
          values: {
            start: { status: "addon", text: "+ 19 € / Mo." },
            studio: { status: "included" },
            premium: { status: "included" },
          },
        },
        {
          key: "appointments_calendar",
          label: "Terminverwaltung",
          description: "Ressourcen- & Terminplanung im Studio, Buchungsübersicht",
          values: {
            start: { status: "included" },
            studio: { status: "included" },
            premium: { status: "included" },
          },
        },
        {
          key: "multichannel",
          label: "Multi-Channel-Kommunikation",
          description: "WhatsApp Business, SMS & Postbriefe direkt aus m1member (E-Mail inklusive)",
          values: {
            start: { status: "addon", text: "+ 19 € / Mo." },
            studio: { status: "included" },
            premium: { status: "included" },
          },
        },
      ],
    },
    {
      key: "staff_sales",
      title: tr("Staff & Sales"),
      rows: [
        {
          key: "personal",
          label: "Personal- & Dienstplanung (HR)",
          description: "Schichtplanung, Urlaubsverwaltung, Zeiterfassung & Mindestlohn-Wächter",
          values: {
            start: { status: "addon", text: "+ 29 € / Mo." },
            studio: { status: "addon", text: "+ 29 € / Mo." },
            premium: { status: "included" },
          },
        },
        {
          key: "leads_pipeline",
          label: "Vertriebs-Pipelines & Lead-Management",
          description: "Mehrstufige Pipelines, Phasen, Wiedervorlagen & Konversionsquoten",
          values: {
            start: { status: "addon", text: "+ 49 € / Mo." },
            studio: { status: "addon", text: "+ 49 € / Mo." },
            premium: { status: "included" },
          },
        },
        {
          key: "automation",
          label: "Workflow-Automatisierung & Boni",
          description: "regelbasierte Trigger (Geburtstage, Inaktivität) & Empfehlungsprämien",
          values: {
            start: { status: "addon", text: "+ 29 € / Mo." },
            studio: { status: "addon", text: "+ 29 € / Mo." },
            premium: { status: "included" },
          },
        },
      ],
    },
    {
      key: "ai_support",
      title: tr("AI & Support"),
      rows: [
        {
          key: "qeddy",
          label: "Qeddy KI-Bedienhilfe",
          description: "kontextsensitive Bedienhilfe auf Basis geprüfter m1member-Hilfe",
          values: {
            start: { status: "included" },
            studio: { status: "included" },
            premium: { status: "included" },
          },
        },
        {
          key: "ai_inbound",
          label: "KI-Sachbearbeiter (Verträge & Posteingang)",
          description: "monatliches Inklusivkontingent",
          values: valuesByPlan(plans, (plan) => {
            const quota = describePlanAiQuota(plan, tr);
            return plan.priceOnRequest || !quota
              ? { status: "custom", text: tr("individual"), hint: tr("as needed") }
              : { status: "custom", text: quota, hint: tr("included") };
          }),
        },
        {
          key: "ai_overage",
          label: "Folgevorgänge bei Mehrverbrauch",
          description: "automatische Fortführung ohne Unterbrechung",
          values: {
            start: { status: "custom", text: "0,20 €", hint: "je Vorgang" },
            studio: { status: "custom", text: "0,20 €", hint: "je Vorgang" },
            premium: { status: "custom", text: "0,15 €", hint: "je Vorgang" },
          },
        },
        {
          key: "support_level",
          label: "Support-Level & SLA",
          description: "Reaktionszeiten und Ansprechpartner",
          values: {
            start: { status: "custom", text: "Standard", hint: "E-Mail-Support" },
            studio: { status: "custom", text: "Standard", hint: "E-Mail & Chat" },
            premium: { status: "custom", text: "prioritär", hint: "bevorzugte Bearbeitung" },
          },
        },
        {
          key: "migration",
          label: "CenterCom-Altdatenübernahme",
          description: "Mitglieder & Leads, Verträge, SEPA-Mandate, Salden & Termine",
          values: {
            start: { status: "custom", text: "599 €", hint: "einmalig zubuchbar" },
            studio: { status: "custom", text: "599 €", hint: "einmalig zubuchbar" },
            premium: { status: "custom", text: "599 €", hint: "einmalig zubuchbar" },
          },
        },
      ],
    },
  ];
}
